import React, { FC, useCallback, useState } from "react";
import { ButtonProps, Dialog, Input, URLInput } from "@xapp/components";

import { BasicIntegrationCard, IntegrationCardProp } from "./BasicIntegrationCard";

export interface BoberdooConnectionProps {
    integration: "boberdoo";
    apiKey: string;
    endpoint: string;
    leadType: string;
    source: string;
    apiAction?: string;
    defaultLandingPage?: string;
    defaultProject?: string;
    mode?: string;
}

export interface BoberdooIntegrationCardProps extends IntegrationCardProp {
    apiKey?: string;
    endpoint?: string;
    leadType?: string;
    source?: string;
    apiAction?: string;
    defaultLandingPage?: string;
    defaultProject?: string;
    mode?: string;

    onConnect?(props: BoberdooConnectionProps): void;
}

export const BoberdooIntegrationCard: FC<BoberdooIntegrationCardProps> = (props) => {

    const { checked, disabled, isLinked, isActive, onConnect, onDisconnect } = props;

    const [apiKey, setApiKey] = useState<string>(props.apiKey || "");
    const [endpoint, setEndpoint] = useState<string>(props.endpoint || "");
    const [leadType, setLeadType] = useState<string>(props.leadType || "");
    const [source, setSource] = useState<string>(props.source || "");
    const [apiAction, setApiAction] = useState<string>(props.apiAction || "");
    const [defaultLandingPage, setDefaultLandingPage] = useState<string>(props.defaultLandingPage || "");
    const [defaultProject, setDefaultProject] = useState<string>(props.defaultProject || "");
    const [mode, setMode] = useState<string>(props.mode || "");
    const [showDialog, setShowDialog] = useState<boolean>(false);

    const resetForm = useCallback(() => {
        setApiKey(props.apiKey || "");
        setEndpoint(props.endpoint || "");
        setLeadType(props.leadType || "");
        setSource(props.source || "");
        setApiAction(props.apiAction || "");
        setDefaultLandingPage(props.defaultLandingPage || "");
        setDefaultProject(props.defaultProject || "");
        setMode(props.mode || "");
    }, [props.apiKey, props.endpoint, props.leadType, props.source, props.apiAction, props.defaultLandingPage, props.defaultProject, props.mode]);

    const handleCloseDialog = useCallback(() => {
        resetForm();
        setShowDialog(false);
    }, [resetForm]);

    const handleBoberdooClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>): void => {
        e.preventDefault();

        if (typeof checked === "boolean") {
            if (typeof props.onClick === "function") {
                props.onClick(e);
            }
        } else {
            setShowDialog(true);
        }
    }, [checked, props.onClick]);

    const handleConnectBoberdoo = useCallback(() => {
        if (typeof onConnect === "function") {
            onConnect({
                integration: "boberdoo",
                apiKey,
                endpoint,
                leadType,
                source,
                apiAction: apiAction || undefined,
                defaultLandingPage: defaultLandingPage || undefined,
                defaultProject: defaultProject || undefined,
                mode: mode || undefined,
            });
            setShowDialog(false);
        }
    }, [onConnect, apiKey, endpoint, leadType, source, apiAction, defaultLandingPage, defaultProject, mode]);

    const isValidEndpoint = useCallback((url: string): boolean => {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    }, []);

    const actions: ButtonProps[] = [
        {
            label: "Close",
            variation: "secondary",
            onClick: handleCloseDialog
        }
    ];

    if (typeof onConnect === "function") {
        actions.push({
            label: "Connect",
            variation: "primary",
            disabled: !apiKey || !endpoint || !leadType || !source || !isValidEndpoint(endpoint),
            onClick: handleConnectBoberdoo
        })
    }

    return (
        <>
            <BasicIntegrationCard
                checked={checked}
                disabled={disabled}
                imageUrl="/img/integrations/boberdoo/boberdoo-logo.png"
                text="boberdoo"
                href={() => "/integrations/boberdoo"}
                onClick={handleBoberdooClick}
                isLinked={isLinked}
                isActive={isActive}
                onDisconnect={onDisconnect}
            />
            <Dialog
                active={showDialog}
                actions={actions}
                title="boberdoo Setup"
                onClose={handleCloseDialog}
            >
                <p>Enter your boberdoo lead distribution details below.</p>
                <Input label="API Key" type="password" value={apiKey} onChange={(val) => setApiKey(String(val))} />
                <URLInput label="Endpoint URL" value={endpoint} onChange={(val) => setEndpoint(String(val))} />
                <Input label="Lead Type" value={leadType} onChange={(val) => setLeadType(String(val))} />
                <Input label="Source" value={source} onChange={(val) => setSource(String(val))} />
                <Input label="API Action (Optional)" value={apiAction} onChange={(val) => setApiAction(String(val))} />
                <Input label="Default Landing Page (Optional)" value={defaultLandingPage} onChange={(val) => setDefaultLandingPage(String(val))} />
                <Input label="Default Project (Optional)" value={defaultProject} onChange={(val) => setDefaultProject(String(val))} />
                <Input label="Mode (Optional)" value={mode} onChange={(val) => setMode(String(val))} />
            </Dialog>
        </>
    );
}
